// ── Shared drop-position helpers for the hand-rolled mouse-drag idiom used across
// every board's drag-and-drop (sprint-board.js, kanban.js cards + column headers,
// backlog.js, sprint-planning.js) and settings.js's Workflow status rows (dropIndex()
// only, TPT9184): clone + placeholder + `.X-hidden` on the dragged
// original (never removed from the DOM, only hidden), 5px move threshold, no HTML5
// DnD. Extracted C1360 to fix two independent bugs that existed at every call site
// (see ai/architecture/tt-web-board.md § Drag-and-drop drop position for the writeup):
//
//  - the insertion-point hit-test compared only one axis (usually Y), which breaks
//    down for a multi-column CSS grid container (sprint-board.js's `.sprint-cards`) —
//    every card in a row shares the same `top`, so the loop always matched the row's
//    first card and the placeholder could only ever land at a row boundary.
//  - the drop-index math counted raw DOM children (`[...container.children].indexOf
//    (placeholder)`), which includes the still-hidden dragged original and trailing
//    chrome (a quick-create row, in sprint-board.js) that the target task array does
//    not — silently overshooting the drop by one slot on same-container reorders.

// ── Insertion point (drag-in-progress hit-test) ──
//
// Which sibling should the placeholder be inserted before, for a pointer at (x, y)?
// `items` must already exclude the dragged original (callers pass
// `[...container.querySelectorAll('.foo:not(.foo-hidden)')]`). Returns the element to
// insertBefore, or null meaning "append at the end".
//
//   axis 'y'    vertical list — before the first item whose vertical midpoint is
//               below the pointer.
//   axis 'x'    horizontal strip — same test on the X axis (kanban column headers).
//   axis 'grid' row-major wrapping grid (sprint-board.js's `.sprint-cards`) — an
//               earlier row wins outright; within the pointer's own row the
//               horizontal midpoint decides. Auto-degrades to the 'y' rule when the
//               grid has actually collapsed to a single column (narrow viewport,
//               see styles.css's `.sprint-cards { grid-template-columns: 1fr }`
//               media query) — detected by checking whether any later item's top
//               is still within the first item's own row band.
export function findInsertBefore(items, x, y, axis = 'y') {
  if (items.length === 0) return null;

  if (axis === 'grid') {
    const firstRect = items[0].getBoundingClientRect();
    const collapsedToOneColumn = items.every((el, i) => {
      if (i === 0) return true;
      const r = el.getBoundingClientRect();
      return r.top >= firstRect.bottom || r.left <= firstRect.left;
    });
    if (!collapsedToOneColumn) {
      for (const el of items) {
        const r = el.getBoundingClientRect();
        const earlierRow = y < r.top;
        const sameRowLeftHalf = y < r.bottom && x < r.left + r.width / 2;
        if (earlierRow || sameRowLeftHalf) return el;
      }
      return null;
    }
    // Collapsed to one column — fall through to the plain vertical-list rule below.
  }

  for (const el of items) {
    const r = el.getBoundingClientRect();
    if (axis === 'x') {
      if (x < r.left + r.width / 2) return el;
    } else {
      if (y < r.top + r.height / 2) return el;
    }
  }
  return null;
}

// ── Drop index (drag-end index math) ──
//
// How many real, visible, in-scope siblings precede the placeholder inside
// `container` — the index to splice the dragged task into its target array at.
//
// MUST be called BEFORE the caller's drag-cleanup step removes `hiddenClass` from
// the dragged original — this counts elements NOT carrying `hiddenClass`, so once
// cleanup un-hides the original it would start counting itself.
//
// `selector` scopes the count to real item elements (excludes the placeholder
// itself, trailing chrome like a quick-create trigger, etc.). `filter`, when given,
// further restricts to elements that belong to the same drop target as the
// placeholder (e.g. sprint-board.js flat mode: only cards already at the target
// sprint's priority).
export function dropIndex(container, placeholder, { selector, hiddenClass, filter }) {
  if (!container) return 0;
  const children = [...container.children];
  const placeholderPos = children.indexOf(placeholder);
  if (placeholderPos === -1) return 0;
  return children.slice(0, placeholderPos).filter((el) => {
    if (!el.matches?.(selector)) return false;
    if (hiddenClass && el.classList.contains(hiddenClass)) return false;
    if (filter && !filter(el)) return false;
    return true;
  }).length;
}
